import {
  Component,
  ElementRef,
  Input,
  InputSignalWithTransform,
  OutputEmitterRef,
  QueryList,
  ViewChildren,
  booleanAttribute,
  input,
  output
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { Mapper, NgUtils, TiimePipesModule } from 'tiime-expert-utils';
import { TiimeButtonModule, TiimeIconModule, TiimeTableModule, TiimeTooltipModule } from 'tiime-material';

import { PeriodicityEnum } from '@enums/periodicity.enum';
import { PrestationForm } from '@gi/forms/prestation-form';
import { TimeTrackingsTask } from '@models/time-trackings-task';
import { BooleanInputType } from '@modules/core/types/boolean-input.type';
import { ListErrorComponent } from '@shared-components/list-error/list-error.component';
import { DigitOnlyDirective } from '@shared-directives/digit-only/digit-only.directive';
import { DiscountPeriodicityLabelPipe } from '@shared-pipes/discount-periodicity-label/discount-periodicity-label.pipe';
import { PrestationsGroupByOfferPipe } from '@shared-pipes/prestations-group-by-offer/prestations-group-by-offer.pipe';
import { PeriodicitySelectComponent } from '@shared-selectors/periodicity-select/periodicity-select.component';
import { TimeTrackingsTaskSelectComponent } from '@shared-selectors/time-trackings-task-select/time-trackings-task-select.component';

@Component({
  selector: 'app-prestations-form-table',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TiimeButtonModule,
    TiimeIconModule,
    TiimeTableModule,
    TiimeTooltipModule,
    TiimePipesModule,
    PeriodicitySelectComponent,
    DigitOnlyDirective,
    TimeTrackingsTaskSelectComponent,
    PrestationsGroupByOfferPipe,
    DiscountPeriodicityLabelPipe,
    ListErrorComponent
  ],
  templateUrl: './prestations-form-table.component.html',
  styleUrls: ['./prestations-form-table.component.scss']
})
export class PrestationsFormTableComponent {
  @Input({ required: true }) prestationForms: PrestationForm[];
  @Input({ required: true }) tasks: TimeTrackingsTask[];
  readonly withHeader: InputSignalWithTransform<boolean, BooleanInputType> = input<boolean, BooleanInputType>(false, {
    transform: booleanAttribute
  });

  readonly removePrestation: OutputEmitterRef<number> = output<number>();
  readonly removeModelOffer: OutputEmitterRef<number> = output<number>();

  @ViewChildren(TimeTrackingsTaskSelectComponent) taskSelectComponent: QueryList<TimeTrackingsTaskSelectComponent>;
  @ViewChildren('labelInput') labelInput: QueryList<ElementRef<HTMLInputElement>>;

  readonly trackByFormId = NgUtils.trackByFormId;
  readonly displayedColumns = ['task', 'label', 'amountExcludingTaxed', 'periodicity', 'actions'];
  readonly periodicities: PeriodicityEnum[] = [
    PeriodicityEnum.unit,
    PeriodicityEnum.monthly,
    PeriodicityEnum.quarterly,
    PeriodicityEnum.halfYearly,
    PeriodicityEnum.annual
  ];

  readonly mapToDisplayOfferRow: Mapper<PrestationForm, boolean> = (prestationForm: PrestationForm, i: number) => {
    const index = this.prestationForms.findIndex(
      form => form.contractOffer.value?.offer?.id === prestationForm.contractOffer.value?.offer?.id
    );
    return index === i;
  };

  emitRemovePrestation(index: number): void {
    this.removePrestation.emit(index);
  }

  emitRemoveModelOffer(modelOfferId: number): void {
    this.removeModelOffer.emit(modelOfferId);
  }

  addPrestationDiscount(prestationForm: PrestationForm): void {
    prestationForm.addDiscount();
  }

  removePrestationDiscount(prestationForm: PrestationForm): void {
    prestationForm.removeDiscount();
  }

  openLastTaskSelectPanel(): void {
    setTimeout(() => this.taskSelectComponent?.last?.selectComponent?.openPanel());
  }

  taskChange(prestationForm: PrestationForm, index: number): void {
    prestationForm.onTaskChange();
    this.selectLabelInput(index);
  }

  periodicityChange(prestationForm: PrestationForm): void {
    if (prestationForm.periodicity.value === PeriodicityEnum.unit && prestationForm.discountOccurrence.value) {
      prestationForm.discountOccurrence.reset();
    }
  }

  private selectLabelInput(index: number): void {
    setTimeout(() => this.labelInput?.find((_, inputIndex) => inputIndex === index)?.nativeElement?.select());
  }
}
